import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { userAPI } from '../services/api';

type Status =
  | { kind: 'validating' }
  | { kind: 'invalid'; reason: string }
  | { kind: 'ready' }
  | { kind: 'submitting' }
  | { kind: 'success' };

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [status, setStatus] = useState<Status>({ kind: 'validating' });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token) {
        setStatus({
          kind: 'invalid',
          reason: 'Missing reset token. Please request a new password reset link.',
        });
        return;
      }
      try {
        const result = await userAPI.validateResetToken(token);
        if (cancelled) return;
        if (result.valid) {
          setStatus({ kind: 'ready' });
        } else {
          setStatus({
            kind: 'invalid',
            reason:
              result.error ||
              'This reset link is invalid or has expired. Please request a new one.',
          });
        }
      } catch (err: any) {
        if (cancelled) return;
        setStatus({
          kind: 'invalid',
          reason:
            err?.message ||
            'We could not validate this reset link. Please request a new one.',
        });
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setStatus({ kind: 'submitting' });
    try {
      await userAPI.resetPassword(token, password);
      setStatus({ kind: 'success' });
    } catch (err: any) {
      setError(err?.message || 'Failed to reset password. Please try again.');
      setStatus({ kind: 'ready' });
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-md p-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
          Reset your password
        </h1>
        <p className="text-sm text-gray-600 mb-6">
          Choose a strong new password for your Piezit account.
        </p>

        {status.kind === 'validating' && (
          <div className="flex items-center gap-3 text-gray-600">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
            <span>Validating your reset link…</span>
          </div>
        )}

        {status.kind === 'invalid' && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-red-800">
                <p className="font-medium mb-1">Reset link unavailable</p>
                <p>{status.reason}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Return to Piezit
            </button>
          </div>
        )}

        {(status.kind === 'ready' || status.kind === 'submitting') && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pl-10 pr-10"
                  placeholder="At least 8 characters"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm new password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field pl-10"
                  placeholder="Re-enter your new password"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={status.kind === 'submitting'}
              className="w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
            >
              {status.kind === 'submitting' ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Updating password…</span>
                </>
              ) : (
                <span>Update password</span>
              )}
            </button>
          </form>
        )}

        {status.kind === 'success' && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-lg p-4">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-green-800">
                <p className="font-medium mb-1">Password updated</p>
                <p>
                  Your password has been changed successfully. You can now sign
                  in with your new password.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
            >
              Continue to Piezit
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
